import { useEffect, useState, useCallback } from 'react';
import ClientService, { IClient } from '../../../../services/Client.service';
import { useTranslation } from 'react-i18next';
import Table, { TableColumn } from '../../../../components/organisms/table';
import MVCHeader from '../../../../components/organisms/mvc-header';
import Pagination, {
	PaginationProps,
} from '../../../../components/organisms/pagination';
import Actions from '../../../../components/molecules/actions';
import ClientsModal from './modal';
import { ModalProps } from '../../../../components/template/mvc-modal';
import classNames from 'classnames';
import Text from '../../../../components/atoms/text';
import ModalConfirmation from '../../../../components/organisms/modal-confirmation';
import SVGIcon from '../../../../components/atoms/icons';
import { NotificationToast } from '../../../../components/organisms/notification-toast';

const initialState: IClient = {
	corporateName: '',
	fantasyName: '',
	isActive: true,
	moduloAuditoriaAtivado: true,
	processoFluxoUnicoAtivado: true,
	supplyReturnAtivado: true,
	maxDailyMonitoringsAM1: '',
	maxDailyMonitoringsPM1: '',
	maxDailyMonitoringsAM2: '',
	maxDailyMonitoringsPM2: '',
	monitoringFee: '',
	maintenanceFee: '',
	cnpj: '',
	address: '',
	permissions: {
		isMaintenance: false,
		isMonitoring: false,
		isMovement: false,
		isReport: false,
	},
};

const Clients = () => {
	const { t } = useTranslation();

	const modalTitles = {
		create: t('admin.clients.new'),
		edit: t('admin.clients.edit'),
		view: t('admin.clients.view'),
	};

	const [data, setData] = useState<IClient[]>([]);
	const [isDeleteModalVisible, setIsDeleteModalVisible] =
		useState<boolean>(false);
	const [dataToDelete, setDataToDelete] = useState<IClient | null>(null);

	const [paginationOptions, setPaginationOptions] = useState<PaginationProps>(
		{
			pageCurrent: 1,
			pageSize: 10,
			totalItems: 0,
			totalPages: 1,
		}
	);

	const [modalProps, setModalProps] = useState<ModalProps<IClient>>({
		data: initialState,
		title: modalTitles.view,
		mode: 'view',
		isModalVisible: false,
		onClose: (reload?: boolean) => {
			setModalProps((prev) => ({
				...prev,
				isModalVisible: false,
				data: initialState,
			}));
			if (reload) fetchClients();
		},
	});

	const fetchClients = useCallback(async () => {
		const response = await ClientService.getAll({
			pageCurrent: paginationOptions.pageCurrent,
			pageSize: paginationOptions.pageSize,
		});
		if (response) {
			setData(response.data.map((conta) => ({
				...conta,
				moduloAuditoriaAtivado:
					conta.systemPropertie?.moduloAuditoriaAtivado === true,
				processoFluxoUnicoAtivado:
					conta.systemPropertie?.processoFluxoUnicoAtivado === true,
				supplyReturnAtivado:
					conta.systemPropertie?.supplyReturnAtivado === true,
			})));
			setPaginationOptions((prev) => ({
				...prev,
				totalItems: response.totalItems,
				totalPages: response.totalPages,
			}));
		}
	}, [paginationOptions.pageCurrent, paginationOptions.pageSize]);

	useEffect(() => {
		fetchClients();
	}, [fetchClients]);

	const onPaginationUpdate = (pageSize: number, pageCurrent: number) => {
		setPaginationOptions((prev) => ({ ...prev, pageSize, pageCurrent }));
	};

	const onActionsViewClick = useCallback(
		(data: IClient) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.view,
				mode: 'view',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.view]
	);

	const onActionsEditClick = useCallback(
		(data: IClient) => {
			setModalProps((prev) => ({
				...prev,
				title: modalTitles.edit,
				mode: 'edit',
				isModalVisible: true,
				data,
			}));
		},
		[modalTitles.edit]
	);

	const onCreateClick = useCallback(() => {
		setModalProps((prev) => ({
			...prev,
			title: modalTitles.create,
			mode: 'create',
			isModalVisible: true,
			data: initialState,
		}));
	}, [modalTitles.create]);

	const onActionsDeleteClick = (data: IClient) => {
		setDataToDelete(data);
		setIsDeleteModalVisible(true);
	};

	const onDeleteModalClose = () => {
		setIsDeleteModalVisible(false);
		setDataToDelete(null);
	};

	const onDeleteConfirm = async () => {
		if (!dataToDelete || !dataToDelete.id) return;
		await ClientService.delete(dataToDelete.id);
		fetchClients();
		NotificationToast.Success(
			`${t('admin.client')} ${t('alert.success.delete')}`
		);
		onDeleteModalClose();
	};

	const columns: TableColumn<IClient>[] = [
		{ key: 'fantasyName', label: t('admin.clients.fantasy_name') },
		{ key: 'corporateName', label: t('admin.clients.corporate_name') },
		{ key: 'cnpj', label: t('admin.clients.cnpj') },
		{ key: 'address', label: t('misc.address') },
		{
			key: 'maxDailyMonitoringsAM1',
			label: t('admin.clients.monitoring_am'),
			children: (row) => (
				<Text className="text-xs">
					{row.maxDailyMonitoringsAM1} |&nbsp;
					{row.maxDailyMonitoringsAM2}
				</Text>
			),
		},
		{
			key: 'maxDailyMonitoringsPM1',
			label: t('admin.clients.monitoring_pm'),
			children: (row) => (
				<Text className="text-xs">
					{row.maxDailyMonitoringsPM1} |&nbsp;
					{row.maxDailyMonitoringsPM2}
				</Text>
			),
		},
		{ key: 'monitoringFee', label: t('admin.clients.monitoring_fee') },
		{ key: 'maintenanceFee', label: t('admin.clients.maintenance_fee') },
		{
			key: 'moduloAuditoriaAtivado',
			label: t('admin.clients.modulo_auditoria'),
			children: (conta) => (
				<Text className="text-xs">
					{t(conta.moduloAuditoriaAtivado ? 'status.active' : 'status.inactive')}
				</Text>
			),
		},
		{
			key: 'processoFluxoUnicoAtivado',
			label: t('admin.clients.container_tracking'),
			children: (conta) => (
				<Text className="text-xs">
					{t(conta.processoFluxoUnicoAtivado ? 'status.active' : 'status.inactive')}
				</Text>
			),
		},
		{
			key: 'supplyReturnAtivado',
			label: t('admin.clients.supply_return'),
			children: (conta) => (
				<Text className="text-xs">
					{t(conta.supplyReturnAtivado ? 'status.active' : 'status.inactive')}
				</Text>
			),
		},
		{
			key: 'actions',
			label: t('misc.actions'),
			children: (row) => (
				<Actions
					onViewClick={() => onActionsViewClick(row)}
					onEditClick={() => onActionsEditClick(row)}
					onDeleteClick={() => onActionsDeleteClick(row)}
				/>
			),
		},
	];

	return (
		<>
			<div className="flex-grow grid grid-rows-[auto_1fr_auto] p-4 gap-y-4">
				<MVCHeader
					title={t('admin.clients.list')}
					onCreateText={t('admin.clients.new')}
					onCreateClick={onCreateClick}
				/>

				<div className="overflow-x-auto w-full">
					<Table
						data={data}
						columns={columns}
						className={classNames(
							'grid-cols-[auto_auto_auto_auto_auto_auto_auto_auto_auto_auto_auto_auto]'
						)}
					/>
				</div>

				<Pagination
					{...paginationOptions}
					onUpdate={onPaginationUpdate}
				/>
			</div>

			<ClientsModal setModalProps={setModalProps} {...modalProps} />
			<ModalConfirmation
				onConfirm={onDeleteConfirm}
				onCancel={onDeleteModalClose}
				isModalVisible={isDeleteModalVisible}
			>
				<div className="flex flex-col items-center text-center gap-4">
					<SVGIcon
						icon="Error"
						className="h-12 w-auto fill-red stroke-white"
					/>
					<Text>
						{t('alert.confirmation.delete')}
						<b> {dataToDelete?.corporateName}</b>?
					</Text>
				</div>
			</ModalConfirmation>
		</>
	);
};

export default Clients;
